<template>
  <div class="d-inline">
    <button
      v-if="status === 'SCHEDULED' && canVerify"
      type="button"
      class="btn btn-success py-2"
      @click.prevent="confirmVerify"
      :disabled="verifyLoading"
    >
      <i class="fas fa-spin fa-circle-notch" v-if="verifyLoading"></i>
      <i class="fas fa-check-double mr-1" v-else></i> Verify
    </button>

    <button
      v-if="status === 'VERIFIED' && canUnverify"
      type="button"
      class="btn btn-danger py-2"
      @click.prevent="confirmUnverify"
      :disabled="unverifyLoading"
    >
      <i class="fas fa-spin fa-circle-notch" v-if="unverifyLoading"></i>
      <i class="fas fa-undo mr-1" v-else></i> Unverify
    </button>

    <button
      v-if="status !== 'VERIFIED' && canDelete"
      type="button"
      class="btn btn-danger ml-2 py-2"
      @click.prevent="confirmDelete"
      :disabled="deleteLoading"
    >
      <i class="fas fa-spin fa-circle-notch" v-if="deleteLoading"></i>
      <i class="fas fa-trash-alt mr-1" v-else></i> Delete
    </button>
  </div>
</template>

<script>
import Swal from "sweetalert2";

const swalWithBootstrapButtons = Swal.mixin({
  customClass: {
    confirmButton: "btn btn-black ml-4",
    cancelButton: "btn btn-danger",
  },
  buttonsStyling: false,
});

export default {
  name: "SessionWorkflowActions",
  props: {
    status: {
      type: String,
      required: true,
    },
    verifyLoading: {
      type: Boolean,
      default: false,
    },
    unverifyLoading: {
      type: Boolean,
      default: false,
    },
    canVerify: {
      type: Boolean,
      default: false,
    },
    canUnverify: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    deleteLoading: {
      type: Boolean,
      default: false,
    },
    changesMade: {
      type: Boolean,
      default: false,
    },
    showDeleteNotifyOption: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    async confirmVerify() {
      if (this.changesMade) {
        const result = await Swal.fire({
          title: "Changes made to this session",
          text: "You made some changes to this session, do you want to save them?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Save & Verify",
          cancelButtonText: "Verify Only",
          customClass: {
            confirmButton: "btn btn-black",
            cancelButton: "btn btn-theme ml-4",
          },
          buttonsStyling: false,
        });

        if (result.isConfirmed) {
          this.$emit("saveAndVerify");
        } else if (result.dismiss === "cancel") {
          this.$emit("verify");
        }
        return;
      }

      const result = await swalWithBootstrapButtons.fire({
        title: "Verify Session?",
        text: "This will lock the session and trigger billing. This action can only be undone by the super admin.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, verify",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });
      if (result.isConfirmed) {
        this.$emit("verify");
      }
    },
    async confirmUnverify() {
      const result = await swalWithBootstrapButtons.fire({
        title: "Unverify Session?",
        text: "This will unlock the session and reverse billing.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, unverify",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });
      if (result.isConfirmed) {
        this.$emit("unverify");
      }
    },
    async confirmDelete() {
      if (this.showDeleteNotifyOption) {
        const result = await Swal.fire({
          title: "Delete Session",
          text: "Are you sure? You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Delete & Notify",
          denyButtonText: "Delete Only",
          cancelButtonText: "Cancel",
          customClass: {
            confirmButton: "btn btn-danger ml-2",
            denyButton: "btn btn-black ml-2",
            cancelButton: "btn btn-outline-danger ml-2",
          },
          buttonsStyling: false,
        });
        if (result.isConfirmed) {
          this.$emit("delete", { sendNotification: true });
        } else if (result.isDenied) {
          this.$emit("delete", { sendNotification: false });
        }
        return;
      }

      const result = await swalWithBootstrapButtons.fire({
        title: "Delete Session?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });
      if (result.isConfirmed) {
        this.$emit("delete", { sendNotification: false });
      }
    },
  },
};
</script>
